// ============================================================================
// chatInfoData.ts — данные правой панели «О чате» (SSoT/SRP).
//   • карточка сущности контекстного чата (context_type/code/id) — те же RPC,
//     что у списков/форм: metadata.listTableData + listFields (+ getRegister,
//     entity.getEntity); представления ссылок и статуса резолвит СЕРВЕР
//     (`{col}_presentation`), клиент НЕ подставляет UUID вместо имени;
//   • роли участников — entity.listEntityRoles (кэш на время сессии);
//   • вложения/ссылки — из ленты чата (ListMessages уже отдаёт вложения);
//   • офлайн-фёрст: карточка сущности кладётся в durable-кэш чтения.
// ============================================================================

import type { Entity, Field } from '@zdc/sdk';
import { getSDK } from '@/store/sdkStore';
import { logSwallowed } from '@/lib/errors';
import { readThrough } from '@/lib/offlineCache';
import { resolveObjectSynonym } from '@/lib/resolveObjectName';
import { enumRefOf, loadEnumOptions } from '@/lib/enumOptions';
import type { EnumOption } from '@/components/forms/fieldWidgets';
import { formatValue } from '@/lib/format';
import { fileExt } from '@/lib/fileName';
import { tokenizeTextLinks } from '@/lib/hydraLinks';

export interface ChatEntityField {
  label: string;
  value: string;
}

export interface ChatEntityInfo {
  typeLabel: string;
  objectLabel: string;
  title: string;
  status: string;
  description: string;
  fields: ChatEntityField[];
}

export interface ChatEntityInfoResult {
  info: ChatEntityInfo;
  fromCache: boolean;
}

export interface ChatInfoMessage {
  id: string;
  senderId: string;
  text: string;
  createdAt: string;
  isDeleted?: boolean;
  attachments?: { id: string; name: string; size: number; fileType?: string }[];
}

export interface ChatAttachment {
  id: string;
  name: string;
  size: number;
  fileType: string;
  messageId: string;
  createdAt: string;
  senderId: string;
}

export interface ChatLink {
  href: string;
  label: string;
  messageId: string;
  createdAt: string;
  senderId: string;
}

const TYPE_LABELS: Record<string, string> = {
  catalog: 'Справочник',
  document: 'Документ',
  order: 'Заявка',
  task: 'Задача',
  register: 'Регистр',
  entity: 'Пользователь',
};

const ENTITY_FIELD_LABELS: Array<[keyof Entity, string]> = [
  ['name', 'Имя'],
  ['login', 'Логин'],
  ['email', 'Электронная почта'],
  ['phone', 'Телефон'],
  ['city', 'Город'],
  ['birthDate', 'Дата рождения'],
];

/** Служебные колонки, которые не показываем в «О сущности». */
const SERVICE_COLUMNS = new Set([
  'id', 'version', 'modified_at', 'created_at', 'updated_at', 'created_by', 'updated_by',
  'is_marked', 'is_group', 'is_posted', 'posted_at', 'parent_id', 'owner_id',
  'status', 'status_code', 'status_changed_at', 'name', 'number', 'description',
  'title', 'value', 'code',
]);

function pick(...values: string[]): string {
  for (const v of values) {
    if (v && v.trim()) return v;
  }
  return '';
}

function presentation(values: Record<string, string>, column: string): string {
  const p = values[`${column}_presentation`];
  if (p !== undefined && p !== '') return p;
  return values[column] || '';
}

function displayValue(
  field: Field | undefined,
  raw: string,
  enums: Record<string, EnumOption[]>,
): string {
  if (!raw) return '';
  if (!field) return raw;
  const enumCode = enumRefOf(field);
  if (enumCode) {
    for (const option of enums[enumCode] || []) {
      if (typeof option === 'string') {
        if (option === raw) return option;
      } else if (option.value === raw) {
        return option.label;
      }
    }
    return raw;
  }
  if (field.dataType === 'boolean') {
    return raw === 'true' || raw === '1' ? 'Да' : 'Нет';
  }
  return formatValue(raw, field.format, field.dataType);
}

/** Физическая таблица записи по типу контекста (регистр накопления — таблица движений). */
async function tableForContext(contextType: string, contextCode: string): Promise<string> {
  const type = contextType.toLowerCase();
  if (type === 'register') {
    const reg = await getSDK().metadata.getRegister({ id: contextCode });
    return (reg.movementsTable || reg.code || contextCode).toLowerCase();
  }
  return contextCode.toLowerCase();
}

async function loadEntityInfo(contextId: string): Promise<ChatEntityInfo> {
  const e = await getSDK().entity.getEntity({ id: contextId });
  const fields: ChatEntityField[] = [];
  for (const [key, label] of ENTITY_FIELD_LABELS) {
    const raw = String(e[key] || '');
    if (raw) fields.push({ label, value: raw });
  }
  if (e.roles.length > 0) fields.push({ label: 'Роли', value: e.roles.join(', ') });
  return {
    typeLabel: TYPE_LABELS.entity,
    objectLabel: 'Пользователь',
    title: pick(e.name, e.login, 'Пользователь'),
    status: e.isActive ? 'Активен' : 'Заблокирован',
    description: '',
    fields,
  };
}

async function loadRecordInfo(
  contextType: string,
  contextCode: string,
  contextId: string,
): Promise<ChatEntityInfo> {
  const type = contextType.toLowerCase();
  const table = await tableForContext(contextType, contextCode);
  const data = await getSDK().metadata.listTableData({
    schema: 'public',
    table,
    pageSize: 1,
    page: 0,
    filters: [{ column: 'id', value: contextId }],
  });
  const row = data.rows[0];
  if (!row) throw new Error('Запись не найдена или нет прав на чтение');
  const values: Record<string, string> = {};
  for (const v of row.values) values[v.column] = v.value;

  const fieldList = await getSDK().metadata
    .listFields({ entityType: type, entityCode: contextCode })
    .catch((e) => {
      logSwallowed(e, 'components/chat/chatInfoData.ts:listFields');
      return null;
    });
  const metaFields = fieldList ? fieldList.items : [];
  const enums = await loadEnumOptions(metaFields).catch((e) => {
    logSwallowed(e, 'components/chat/chatInfoData.ts:enums');
    return {};
  });
  const metaByColumn = new Map(metaFields.map((f) => [f.code, f]));

  const statusCol = ['status_code', 'status'].find((c) => (values[c] || '').trim() !== '') || '';
  const status = statusCol ? presentation(values, statusCol) : '';

  const fields: ChatEntityField[] = [];
  for (const f of metaFields) {
    if (f.isActive === false || f.hiddenByDefault) continue;
    if (SERVICE_COLUMNS.has(f.code)) continue;
    const raw = presentation(values, f.code);
    if (!raw) continue;
    fields.push({ label: f.synonym || f.name || f.code, value: displayValue(f, raw, enums) });
    if (fields.length >= 8) break;
  }
  if (fields.length === 0) {
    for (const col of data.columns) {
      if (SERVICE_COLUMNS.has(col.name)) continue;
      const raw = presentation(values, col.name);
      if (!raw) continue;
      fields.push({ label: col.name, value: raw });
      if (fields.length >= 8) break;
    }
  }

  let objectLabel = contextCode;
  try {
    objectLabel = await resolveObjectSynonym(type, contextCode);
  } catch (e) {
    logSwallowed(e, 'components/chat/chatInfoData.ts:synonym');
  }

  return {
    typeLabel: TYPE_LABELS[type] || type,
    objectLabel,
    title: pick(
      values.name,
      values.title,
      values.value,
      values.number,
      values.code,
      'Запись',
    ),
    status,
    description: values.description || '',
    fields,
  };
}

/** Карточка сущности объектного чата (офлайн — последняя сохранённая). */
export async function loadChatEntityInfo(
  contextType: string,
  contextCode: string,
  contextId: string,
): Promise<ChatEntityInfoResult> {
  const key = `chat:entity:${contextType}:${contextCode}:${contextId}`;
  const res = await readThrough<ChatEntityInfo>(key, async () => {
    if (contextType.toLowerCase() === 'entity') return loadEntityInfo(contextId);
    return loadRecordInfo(contextType, contextCode, contextId);
  });
  return { info: res.data, fromCache: res.fromCache };
}

const roleCache = new Map<string, string[]>();

/** Роли участников (для блока «Участники»); необязательные — ошибки в журнал. */
export async function loadMemberRoles(ids: string[]): Promise<Record<string, string[]>> {
  const unique = Array.from(new Set(ids.filter(Boolean)));
  const pairs = await Promise.all(unique.map(async (id): Promise<[string, string[]]> => {
    const cached = roleCache.get(id);
    if (cached) return [id, cached];
    try {
      const r = await getSDK().entity.listEntityRoles({ entityId: id });
      const names = r.items.map((x) => x.roleName).filter(Boolean);
      roleCache.set(id, names);
      return [id, names];
    } catch (e) {
      logSwallowed(e, 'components/chat/chatInfoData.ts:roles');
      return [id, []];
    }
  }));
  const out: Record<string, string[]> = {};
  for (const [id, roles] of pairs) out[id] = roles;
  return out;
}

/** Уникальные вложения ленты (новые первыми). */
export function collectChatAttachments(messages: ChatInfoMessage[]): ChatAttachment[] {
  const seen = new Set<string>();
  const out: ChatAttachment[] = [];
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    for (const a of m.attachments || []) {
      if (!a.id || seen.has(a.id)) continue;
      seen.add(a.id);
      out.push({
        id: a.id,
        name: a.name || 'Файл',
        size: Number(a.size || 0),
        fileType: a.fileType || '',
        messageId: m.id,
        createdAt: m.createdAt,
        senderId: m.senderId,
      });
    }
  }
  return out;
}

function linkLabel(href: string, text: string): string {
  if (href.startsWith('doc:')) return `Справка: ${href.slice(4)}`;
  const label = text.trim();
  if (label && label !== href) return label;
  return href.replace(/^https?:\/\//i, '');
}

/** Уникальные ссылки из текстов сообщений (новые первыми). */
export function collectChatLinks(messages: ChatInfoMessage[]): ChatLink[] {
  const seen = new Set<string>();
  const out: ChatLink[] = [];
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.isDeleted) continue;
    for (const token of tokenizeTextLinks(m.text)) {
      if (!token.link) continue;
      const href = token.link.trim();
      if (!href || seen.has(href)) continue;
      seen.add(href);
      out.push({
        href,
        label: linkLabel(href, token.text),
        messageId: m.id,
        createdAt: m.createdAt,
        senderId: m.senderId,
      });
    }
  }
  return out;
}

/** Расширения, которые открывает существующий FilePreviewModal (SSoT). */
const PREVIEWABLE_EXT = new Set([
  'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'avif',
  'pdf', 'xlsx', 'xlsm', 'csv', 'tsv', 'docx',
  'md', 'txt', 'log', 'json', 'ts', 'tsx', 'js', 'jsx', 'go', 'py', 'sql',
  'sh', 'bat', 'ps1', 'yaml', 'yml', 'xml', 'html', 'css', 'bsl', 'ini',
]);

export function isPreviewableAttachment(name: string): boolean {
  return PREVIEWABLE_EXT.has(fileExt(name));
}

export function formatBytes(size: number): string {
  if (!size || size < 0) return '';
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} МБ`;
  if (size >= 1024) return `${Math.round(size / 1024)} КБ`;
  return `${size} Б`;
}

export function formatStamp(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  return new Date(t).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}


